import { service } from '@ember/service';

import type { RecordData, Service } from 'webapp-types';
import { ModelName, type TeamSectionItemType } from 'webapp/enums/enums';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import TeamSectionItem from 'webapp/models-custom/team-section-item';
import type TeamSectionRecord from 'webapp/models/team-section';

import type { TeamSectionItemSnapshot } from 'webapp/services/model-manager/team-sections';

export default class TeamSection extends BufferedModel<
	TeamSectionRecord,
	RecordData.TeamSection.Serialized
> {
	@service('model-manager/team-section-items')
	declare mmTeamSectionItemsService: Service.ModelManager.TeamSectionItems;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;

	// ------- props from / to ED record ------

	get id() {
		return this.model.id;
	}

	get name(): string {
		return this.getBuffered('name');
	}

	set name(value: string) {
		this.setBuffered('name', value);
	}

	get description(): string {
		return this.getBuffered('description');
	}

	set description(value: string) {
		this.setBuffered('description', value);
	}

	get position(): number {
		return this.getBuffered('position');
	}

	set position(value: number) {
		this.setBuffered('position', value);
	}

	get teamId(): number {
		return this.model.teamId;
	}

	_items: TeamSectionItem[] = [];

	get items(): TeamSectionItem[] {
		const itemRecords = this.mmTeamSectionItemsService.getRecordsForSection(this.numericId!);
		this.mmTeamSectionItemsService.updateModelCache(
			this._items,
			itemRecords,
			(model) => new TeamSectionItem({ model }),
		);
		return this._items;
	}

	get isFull() {
		return this.items.length >= TeamSection.maxItems;
	}

	hasRoomFor(itemCount: number): boolean {
		return this.items.length + itemCount <= TeamSection.maxItems;
	}

	addItem(
		itemType: TeamSectionItemType,
		itemId?: number,
		sectionItemMetricData?: RecordData.TeamSectionItem.ObjectData,
	) {
		const item: TeamSectionItemSnapshot = {
			objectType: itemType,
		};

		if (itemId) {
			item.objectId = itemId;
		}
		if (sectionItemMetricData) {
			item.objectData = { ...sectionItemMetricData };
		}

		return this.mmTeamSectionsService.batchAddItems(this, [item]);
	}

	// ------- other props ------

	static maxItems = 30;

	static override modelName = ModelName.TEAM_SECTION; // store record type
}
